import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper
} from '@mui/material'
import React, { useState, useCallback, useEffect } from 'react'
// import PasswordStrengthBar from 'react-password-strength-bar'
import ReCAPTCHA from 'react-google-recaptcha'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { Navigate, useNavigate } from 'react-router'
import {
  isAnyStringEmpty,
  validateUsername,
  validatePassword,
  validatePhone,
  validateMail,
  validatePasswords,
  isStringNullOrEmpty
} from '../utils/validators'
import { useRequests } from '../requests/useRequests'
import Avatar from '@mui/material/Avatar'
import Link from '@mui/material/Link'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import { backgroundStyle } from '../styles/background'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../state/refresh'

const Register = (): JSX.Element => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const requestMaker = useRequests()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [passwordAgain, setPasswordAgain] = useState('')
  const [phone, setPhone] = useState('')
  const [mail, setMail] = useState('')

  const [usernameError, setUsernameError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordAgainError, setPasswordAgainError] = useState<string | null>(
    null
  )
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [mailError, setMailError] = useState<string | null>(null)
  const [passwordMatchError, setPasswordMatchError] = useState<string | null>(
    null
  )
  const [isActiveButton, setIsActiveButton] = useState(false)

  const [existedUserError, setExistedUserError] = useState<string | null>(null)

  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [isAcceptedCheckbox, setIsAcceptedCheckbox] = useState(false)
  const [isRegisterSuccesfull, setIsRegisterSuccesfull] = useState(false)
  const [isLoginRedirect, setIsLoginRedirect] = useState(false)

  useEffect(() => {
    if (
      !isAnyStringEmpty([
        username,
        password,
        passwordAgain,
        phone,
        mail,
        captchaToken
      ]) &&
      isAcceptedCheckbox
    ) {
      setIsActiveButton(true)
    } else {
      setIsActiveButton(false)
    }
  }, [
    username,
    password,
    passwordAgain,
    phone,
    mail,
    captchaToken,
    isAcceptedCheckbox
  ])

  const handleChangeUsername = async (username: string): Promise<void> => {
    setUsername(username)
    if (username === '') {
      return
    }
    const response = await requestMaker.emailOrUsernameExists(username)

    if (response.data) {
      setUsernameError('User with this username exists')
    } else {
      setUsernameError(null)
    }
    setExistedUserError(null)
  }

  const handlePhoneNumberBlur = async (phoneNumber: string): Promise<void> => {
    const response = await requestMaker.phoneNumberExists(phoneNumber)

    if (response.data) {
      setPhoneError('Phone number exists')
    } else {
      setPhoneError(null)
    }
  }

  const handleEmailBlur = async (email: string): Promise<void> => {
    const response = await requestMaker.emailExists(email)

    if (response.data) {
      setMailError('Email exists')
    } else {
      setMailError(null)
    }
  }

  const handleRegister = useCallback(async () => {
    setUsernameError(null)
    setPasswordError(null)
    setPasswordAgainError(null)
    setPhoneError(null)
    setMailError(null)
    setPasswordMatchError(null)

    if (
      validateUsername(username, setUsernameError) &&
      validatePassword(password, setPasswordError) &&
      validatePassword(passwordAgain, setPasswordAgainError) &&
      validatePhone(phone, setPhoneError) &&
      validateMail(mail, setMailError) &&
      validatePasswords(password, passwordAgain, setPasswordMatchError)
    ) {
      try {
        await requestMaker.registerUser({
          username,
          password,
          phoneNumber: phone,
          email: mail,
          captchaToken
        })
        dispatch(RefreshActions.setToastMessage({ message: 'User created' }))
        setIsRegisterSuccesfull(true)
      } catch (e) {
        setExistedUserError('User with these coordinates exists')
      }
    }
  }, [username, password, passwordAgain, phone, mail, captchaToken, dispatch, requestMaker])

  useEffect(() => {
    if (isRegisterSuccesfull || isLoginRedirect) {
      navigate('/login')
    }
  }, [isRegisterSuccesfull, isLoginRedirect, navigate])

  return (
    <Container component='main' maxWidth='xs'>
      {isLoginRedirect && <Navigate to='/login' />}
      {isRegisterSuccesfull && <Navigate to='/login' />}
      <div style={backgroundStyle} />
      <form
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void handleRegister()
          }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            marginTop: 4,
            marginBottom: 4,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Avatar
              style={{
                margin: '8px',
                width: 56,
                height: 56,
                backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)'
              }}
            >
              <LockOutlinedIcon />
            </Avatar>
            <Typography
              component='h1'
              variant='h5'
              style={{
                marginBottom: '32px',
                fontWeight: 600,
                color: '#2d3748',
                fontSize: '1.75rem'
              }}
            >
              Sign up
            </Typography>
            <TextField
              error={
              !isStringNullOrEmpty(usernameError) ||
              !isStringNullOrEmpty(existedUserError)
            }
              variant='outlined'
              helperText={usernameError}
              value={username}
              onChange={e => {
                setUsernameError(null)
                setUsername(e.target.value)
              }}
              onBlur={e => {
                void handleChangeUsername(e.target.value)
              }}
              margin='normal'
              required
              fullWidth
              id='username'
              label='Username'
              name='username'
              autoComplete='username'
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                }
              }}
            />
            <TextField
              error={!isStringNullOrEmpty(mailError)}
              label='E-mail'
              variant='outlined'
              helperText={mailError}
              value={mail}
              onBlur={e => {
                void handleEmailBlur(e.target.value)
              }}
              onChange={e => {
                setMailError(null)
                setMail(e.target.value)
              }}
              required
              fullWidth
              id='email'
              name='email'
              autoComplete='email'
              margin='normal'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                }
              }}
            />
            <TextField
              error={passwordError !== null}
              variant='outlined'
              helperText={passwordError}
              type='password'
              value={password}
              onChange={e => setPassword(e.target.value)}
              margin='normal'
              required
              fullWidth
              name='password'
              label='Password'
              id='password'
              autoComplete='password'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                }
              }}
            />
            <TextField
              error={
              !isStringNullOrEmpty(passwordAgainError) ||
              !isStringNullOrEmpty(passwordMatchError)
            }
              id='password-again'
              label='Password again'
              variant='outlined'
              helperText={passwordAgainError ?? passwordMatchError}
              type='password'
              value={passwordAgain}
              onChange={e => setPasswordAgain(e.target.value)}
              required
              fullWidth
              name='password-again'
              autoComplete='password-again'
              margin='normal'
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:hover fieldset': { borderColor: '#667eea' },
                  '&.Mui-focused fieldset': { borderColor: '#667eea' }
                }
              }}
            />
            <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
              <PhoneInput
                placeholder='Enter phone number'
                inputClass='material'
                value={phone}
                onBlur={e => {
                  void handlePhoneNumberBlur(e.target.value)
                }}
                onChange={phone => {
                  setPhoneError(null)
                  setPhone(phone)
                }}
                containerStyle={{ width: '100%' }}
                inputStyle={{
                  width: '100%',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  padding: '16.5px 14px'
                }}
              />
              {phoneError !== null && (
                <Alert severity='error' sx={{ mt: 1, borderRadius: '12px' }}>{phoneError}</Alert>
              )}
            </Box>
            <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
              <ReCAPTCHA
                sitekey='6LelLeMiAAAAAITsAnvxxULmIva0mBQB3fA49dwd'
                onChange={token => setCaptchaToken(token)}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isAcceptedCheckbox}
                  onClick={() => setIsAcceptedCheckbox(prev => !prev)}
                  sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }}
                />
            }
              label='I accept VReMa rules'
              sx={{ alignSelf: 'flex-start', mb: 1 }}
            />
            <Button
              type='submit'
              fullWidth
              variant='contained'
              style={{
                marginTop: '16px',
                marginBottom: '16px',
                paddingTop: '12px',
                paddingBottom: '12px',
                borderRadius: '12px',
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 600,
                backgroundImage: !isActiveButton ? 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
              }}
              sx={{
                '&:hover': {
                  backgroundImage: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                }
              }}
              disabled={!isActiveButton}
              onClick={e => {
                e.preventDefault()
                void handleRegister()
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleRegister()
                }
              }}
            >
              Sign Up
            </Button>
            <Grid container justifyContent='center'>
              <Grid item>
                <Link
                  style={{
                    cursor: 'pointer',
                    color: '#667eea',
                    fontWeight: 500,
                    textDecoration: 'none'
                  }}
                  variant='body2'
                  onClick={() => setIsLoginRedirect(true)}
                  sx={{
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Already have an account? Sign In
                </Link>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </form>
    </Container>
  )
}

export default Register
